import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Plus, X } from 'lucide-react';

const ProjectTimelineForm = ({ timeline, milestones, onChange, autoFocusTimeline = false }) => {
  const [timelineData, setTimelineData] = useState(timeline || {});
  const [milestonesData, setMilestonesData] = useState(milestones || []);
  const [showTimeline, setShowTimeline] = useState(!!timeline || autoFocusTimeline);
  const [showMilestones, setShowMilestones] = useState(!!(milestones && milestones.length > 0));
  const [newMilestone, setNewMilestone] = useState({ name: '', due_date: '', hours_allocated: '' });
  const dueDateInputRef = useRef(null);

  useEffect(() => {
    if (autoFocusTimeline) {
      setShowTimeline(true);
      setTimeout(() => {
        dueDateInputRef.current?.focus();
      }, 0);
    }
  }, [autoFocusTimeline]);

  const handleTimelineChange = (field, value) => {
    const newTimeline = { ...timelineData, [field]: value };
    setTimelineData(newTimeline);
    onChange({ timeline: newTimeline, milestones: milestonesData });
  };

  const toggleTimeline = () => {
    const newShowTimeline = !showTimeline;
    setShowTimeline(newShowTimeline);
    if (!newShowTimeline) {
      setTimelineData({});
      onChange({ timeline: null, milestones: milestonesData });
    } else {
      const defaultTimeline = {
        start_date: new Date().toISOString().split('T')[0],
        due_date: '',
        estimated_hours: 40
      };
      setTimelineData(defaultTimeline);
      onChange({ timeline: defaultTimeline, milestones: milestonesData });
    }
  };

  const toggleMilestones = () => {
    const newShowMilestones = !showMilestones;
    setShowMilestones(newShowMilestones);
    if (!newShowMilestones) {
      setMilestonesData([]);
      onChange({ timeline: timelineData, milestones: [] });
    }
  };

  const addMilestone = () => {
    if (!newMilestone.name.trim()) return;
    
    const milestone = {
      name: newMilestone.name,
      due_date: newMilestone.due_date || null,
      completed: false,
      hours_allocated: parseFloat(newMilestone.hours_allocated) || null
    };
    
    const updated = [...milestonesData, milestone];
    setMilestonesData(updated);
    onChange({ timeline: timelineData, milestones: updated });
    setNewMilestone({ name: '', due_date: '', hours_allocated: '' });
  };

  const removeMilestone = (index) => {
    const updated = milestonesData.filter((_, i) => i !== index);
    setMilestonesData(updated);
    onChange({ timeline: timelineData, milestones: updated });
  };

  const toggleMilestoneComplete = (index) => {
    const updated = milestonesData.map((m, i) => 
      i === index ? { ...m, completed: !m.completed } : m
    );
    setMilestonesData(updated);
    onChange({ timeline: timelineData, milestones: updated });
  };

  return (
    <div className="space-y-4">
      {/* Timeline Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showTimeline}
              onChange={toggleTimeline}
              className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
            />
            <Calendar className="w-4 h-4 text-gray-700 dark:text-gray-300 transition-colors" />
            Project Timeline
          </label>
          {showTimeline && (
            <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">Track deadlines & progress</span>
          )}
        </div>

        {showTimeline && (
          <div className="space-y-3 ml-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                  Start Date
                </label>
                <input
                  type="date"
                  value={timelineData.start_date || ''}
                  onChange={(e) => handleTimelineChange('start_date', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                  Due Date
                </label>
                <input
                  type="date"
                  ref={dueDateInputRef}
                  value={timelineData.due_date || ''}
                  onChange={(e) => handleTimelineChange('due_date', e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                Estimated Hours
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={timelineData.estimated_hours || ''}
                onChange={(e) => handleTimelineChange('estimated_hours', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                placeholder="40"
              />
            </div>
          </div>
        )}
      </div>

      {/* Milestones Section */}
      {showTimeline && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={showMilestones}
                onChange={toggleMilestones}
                className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
              />
              Milestones
            </label>
            {showMilestones && (
              <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">{milestonesData.length} milestone(s)</span>
            )}
          </div>

          {showMilestones && (
            <div className="space-y-3 ml-6">
              {/* Existing Milestones */}
              {milestonesData.length > 0 && (
                <div className="space-y-2 mb-3">
                  {milestonesData.map((milestone, index) => (
                    <div
                      key={index}
                      className={`p-3 border rounded-lg transition-colors ${
                        milestone.completed ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2 flex-1">
                          <input
                            type="checkbox"
                            checked={milestone.completed}
                            onChange={() => toggleMilestoneComplete(index)}
                            className="w-5 h-5 mt-1 text-green-600 dark:text-green-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:ring-offset-0 transition-colors cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className={`font-medium text-sm transition-colors ${milestone.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                              {milestone.name}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 transition-colors">
                              {milestone.due_date && (
                                <span>📅 {new Date(milestone.due_date).toLocaleDateString()}</span>
                              )}
                              {milestone.hours_allocated && (
                                <span>⏱️ {milestone.hours_allocated}h</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeMilestone(index)}
                          className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Milestone */}
              <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30 transition-colors">
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Milestone name"
                    value={newMilestone.name}
                    onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={newMilestone.due_date}
                      onChange={(e) => setNewMilestone({ ...newMilestone, due_date: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
                    />
                    <input
                      type="number"
                      placeholder="Hours"
                      value={newMilestone.hours_allocated}
                      onChange={(e) => setNewMilestone({ ...newMilestone, hours_allocated: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                    />
                  </div>
                  <button
                    onClick={addMilestone}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Milestone
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectTimelineForm;
